import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  Button,
  ButtonGroup,
  Card,
  Pagination,
} from "react-bootstrap";
import api from "../api";
import AccountingChart from "../components/AccountingChart";

function AccountingPoshmark() {
  const [rows, setRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [period, setPeriod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Shipping drafts
  const [shippingDrafts, setShippingDrafts] = useState({});

  useEffect(() => {
    fetchAll(1);
    // eslint-disable-next-line
  }, [period, startDate, endDate]);

  /* ---------------- API ---------------- */

  async function fetchAll(newPage = page) {
    try {
      setLoading(true);

      const query = new URLSearchParams({
        period,
        start: startDate || "",
        end: endDate || "",
        page: newPage,
        limit: 50,
      }).toString();

      const res = await api.get(`/accounting/poshmark/sales?${query}`);

      setRows(Array.isArray(res.data.data) ? res.data.data : []);
      setChartData(Array.isArray(res.data.chart) ? res.data.chart : []);
      setSummary(res.data.summary || {});

      setPage(res.data.currentPage || newPage);
      setTotalPages(res.data.totalPages || 1);

    } catch (err) {
      console.error("Poshmark load error:", err);
      setError("Failed to load Poshmark data");
    } finally {
      setLoading(false);
    }
  }

  async function updateShipping(id, value) {
    await api.put("/accounting/poshmark/shipping", {
      id,
      shipping_cost: value === "" ? null : Number(value),
    });

    setShippingDrafts((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });

    fetchAll(page);
  }

  async function toggleExclude(id, excluded) {
    await api.put("/accounting/poshmark/exclude", {
      id,
      excluded,
    });

    fetchAll(page);
  }

  /* ---------------- Pagination UI ---------------- */

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    for (let p = 1; p <= totalPages; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === page}
          onClick={() => fetchAll(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-3">
        <Pagination>
          <Pagination.First disabled={page === 1} onClick={() => fetchAll(1)} />
          <Pagination.Prev disabled={page === 1} onClick={() => fetchAll(page - 1)} />
          {items}
          <Pagination.Next disabled={page === totalPages} onClick={() => fetchAll(page + 1)} />
          <Pagination.Last disabled={page === totalPages} onClick={() => fetchAll(totalPages)} />
        </Pagination>
      </div>
    );
  };

  /* ---------------- helpers ---------------- */

  const num = (v) =>
    v === null || v === undefined || v === "" ? null : Number(v);

  const money = (v) =>
    typeof v === "number" && !isNaN(v) ? `$${v.toFixed(2)}` : "—";

  const percent = (v) =>
    typeof v === "number" && !isNaN(v) ? `${v.toFixed(2)}%` : "—";

  /* ---------------- render ---------------- */

  return (
    <Container fluid className="p-4">
      <h3 className="mb-3">🧥 Poshmark Accounting</h3>

      {/* Filters */}
      <Row className="mb-3 align-items-center">
        <Col md={6}>
          <ButtonGroup>
            {["all", "day", "week", "month"].map((p) => (
              <Button
                key={p}
                variant={period === p ? "primary" : "outline-primary"}
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setPeriod(p);
                  setPage(1);
                }}
              >
                {p === "all"
                  ? "All Time"
                  : p === "day"
                  ? "Today"
                  : p === "week"
                  ? "This Week"
                  : "This Month"}
              </Button>
            ))}

            <Button
              variant={period === "custom" ? "primary" : "outline-secondary"}
              onClick={() => {
                setPeriod("custom");
                setPage(1);
              }}
            >
              Custom
            </Button>
          </ButtonGroup>
        </Col>

        {period === "custom" && (
          <>
            <Col md={2}>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>
          </>
        )}

        <Col md={2}>
          <Button onClick={() => fetchAll(1)} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Apply"}
          </Button>
        </Col>
      </Row>

      {/* Summary */}
      <Row className="mb-4">
        {[
          ["Sales", summary.total_sales, "success"],
          ["COGS", summary.total_cost, "warning"],
          ["Fees", summary.platform_fees, "danger"],
          ["Shipping", summary.total_shipping, "secondary"],
          ["Net Revenue", summary.total_net_revenue, "info"],
          ["Profit", summary.total_profit, "primary"],
          [
  "ROI",
  summary.total_cost > 0
    ? ((summary.total_profit / summary.total_cost) * 100).toFixed(2)
    : "0.00",
  "dark",
],
        ].map(([label, value, variant], i) => (
          <Col md={2} key={i}>
            <Card className={`text-center p-3 shadow-sm border-${variant}`}>
              <h6>{label}</h6>
              <h5>
  {label === "ROI"
    ? `${value}%`
    : money(Number(value || 0))}
</h5>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Chart */}
      <AccountingChart chartData={chartData} period={period} />

      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        <>
          <Table striped bordered hover responsive size="sm" className="mt-4">
            <thead>
              <tr>
                <th>Image</th>
                <th>Order ID</th>
                <th>SKU</th>
                <th>Sales</th>
                <th>COGS</th>
                <th>Platform Fees</th>
                <th>Ship</th>
                <th>Earnings</th>
                <th>Profit</th>
                <th>Margin %</th>
                <th>ROI %</th>
                <th>Qty</th>
                <th>Order Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const sales = num(row.sales);
                const cogs = num(row.cost_of_goods);
                const fees = num(row.platform_fees ?? row.marketplace_fee);
                const earnings = num(row.earnings);
                const profit = num(row.profit);
                const margin = num(row.margin_percent);
                const roi =
  cogs > 0 && profit !== null
    ? (profit / cogs) * 100
    : null;

                return (
                  <tr
                    key={row.id}
                    style={{
                      opacity: row.accounting_excluded ? 0.4 : 1,
                      textDecoration: row.accounting_excluded
                        ? "line-through"
                        : "none",
                    }}
                  >
                    <td style={{ width: 60, textAlign: "center" }}>
  {row.items && row.items.length > 0 ? (
    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
      {row.items.slice(0, 3).map((i, idx) => (
        i.image ? (
          <img
            key={idx}
            src={i.image}
            alt={i.sku}
            style={{
              width: 30,
              height: 30,
              objectFit: "contain",
            }}
          />
        ) : null
      ))}
    </div>
  ) : row.product_image ? (
    <img
      src={row.product_image}
      alt={row.resolved_sku || "Product"}
      style={{
        width: 40,
        height: 40,
        objectFit: "contain",
      }}
    />
  ) : "—"}
</td>

                    <td>{row.external_order_id}</td>
                    <td>{row.resolved_sku ?? "—"}</td>

                    <td>{money(sales)}</td>
                    <td>{money(cogs)}</td>
                    <td>{money(fees)}</td>

                    <td style={{ width: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={
                          shippingDrafts[row.id] ??
                          row.shipping_cost ??
                          ""
                        }
                        onChange={(e) =>
                          setShippingDrafts((prev) => ({
                            ...prev,
                            [row.id]: e.target.value,
                          }))
                        }
                        onBlur={() => {
                          const value = shippingDrafts[row.id];
                          if (value !== undefined) {
                            updateShipping(row.id, value);
                          }
                        }}
                      />
                    </td>

                    <td>{money(earnings)}</td>

                    <td
                      style={{
                        color: profit < 0 ? "red" : "limegreen",
                        fontWeight: 600,
                      }}
                    >
                      {money(profit)}
                    </td>

                    <td>{percent(margin)}</td>
                    <td
  style={{
    color:
      roi === null
        ? "#6c757d"
        : roi >= 0
        ? "limegreen"
        : "red",
    fontWeight: 600,
  }}
>
  {percent(roi)}
</td>
                    <td>{row.quantity ?? 1}</td>

                    <td>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString()
                        : "—"}
                    </td>

                    <td style={{ textAlign: "center" }}>
                      {row.accounting_excluded ? (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => toggleExclude(row.id, false)}
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to mark this sale as canceled?"
                              )
                            ) {
                              toggleExclude(row.id, true);
                            }
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {renderPagination()}
        </>
      )}
    </Container>
  );
}

export default AccountingPoshmark;